import type { AxiosError } from 'axios';

import { WalletAssetsIncompleteError } from '@/hooks/api/errors';
import { QueryKeys } from '@/hooks/api/query-keys';

export function makeNewCache() {
  return {
    groups: {},
    points: {},
    runes: {},
    terms: new Map(),
  };
}

export const toKeyArray = (key: string | readonly unknown[] | undefined): readonly unknown[] => {
  if (!key) {
    return [];
  }

  return Array.isArray(key) ? key : [key];
};

export const arrayIncludesAll = (arr: readonly unknown[], values: string[]): boolean => {
  return values.every((value) => arr.includes(value));
};

export const keyIncludes = (key: string | readonly unknown[] | undefined, ...args: string[]): boolean => {
  const arr = toKeyArray(key);

  return arrayIncludesAll(arr, args);
};

const isHttpError = (error: Error | AxiosError, code: number): boolean => {
  const status = ('response' in error && error.response?.status) || undefined;

  return status === code || error.message?.includes(String(code));
};

export const shouldSkipError = (error: Error | AxiosError, key?: string | readonly unknown[]): boolean => {
  // The fetcher already reports incomplete inventories with pagination context.
  if (error instanceof WalletAssetsIncompleteError) return true;

  // skip 429 error for faucet receive btc - too many requests (claimed recently)
  const isFaucetReceiveBtcOperation = keyIncludes(key, QueryKeys.faucetReceiveBtc);
  if (isFaucetReceiveBtcOperation && isHttpError(error, 429)) {
    return true;
  }

  // skip 404 error for quanta check - user is registered
  const isQuantaCheck = keyIncludes(key, QueryKeys.quantaCheck);
  if (isQuantaCheck && isHttpError(error, 404)) {
    return true;
  }

  // skip 409 error for quanta registration - user already exists
  const isQuantaRegister = keyIncludes(key, QueryKeys.quantaRegister);
  if (isQuantaRegister && isHttpError(error, 409)) {
    return true;
  }

  // skip 404 error for quanta side mission tasks - user hasn't connected Discord/Matrica yet
  const isQuantaSideMissionTasks = keyIncludes(key, QueryKeys.quantaSideMissionTasks);
  if (isQuantaSideMissionTasks && isHttpError(error, 404)) {
    return true;
  }

  // skip 404 error for polling complete-mission-task
  const isQuantaCompleteMissionTaskPolling = keyIncludes(key, QueryKeys.quantaCompleteMissionTaskPolling);
  if (isQuantaCompleteMissionTaskPolling && isHttpError(error, 404)) {
    return true;
  }

  // skip 404 error for polling transaction status (usePollTransactionStatus) - tx not found is expected
  const isTransactionPolling = keyIncludes(key, QueryKeys.transaction);
  if (isTransactionPolling && isHttpError(error, 404)) {
    return true;
  }

  // skip 500 error for telegram notification mutation - server error is expected sometimes
  const isTelegramNotification = keyIncludes(key, QueryKeys.telegramNotification);
  if (isTelegramNotification && isHttpError(error, 500)) {
    return true;
  }

  return false;
};
